import { Component } from '@theme/component'
import { onThemeScroll } from '@theme/events'
import { debounce } from '@theme/utils'

const AnnouncementBarConfig = {
  attributes: {
    hidden: 'data-hidden',
    scrollDirection: 'data-scroll-direction',
    scrolled: 'data-scrolled',
    scrollHide: 'data-scroll-hide'
  },
  direction: {
    down: 'down',
    up: 'up'
  },
  selectors: {
    root: '[data-announcement-bar]'
  },
  thresholds: {
    hideThresholdRatio: 0.9,
    resizeDebounce: 150,
    scrollStopDebounce: 120,
    stickyThresholdRatio: 0.1
  }
}

const root = globalThis

class AnnouncementBar extends Component {
  _cachedHideThreshold = 0
  _cachedStickyThreshold = 0
  _lastScrollState = ''
  _unsubscribeScroll = null
  onResize
  onScrollStop
  scrollDirection = AnnouncementBarConfig.direction.up

  constructor() {
    super()
    this.onScrollEvent = this.onScrollEvent.bind(this)
    this.onScrollStop = debounce(
      this.onScrollStopHandler.bind(this),
      AnnouncementBarConfig.thresholds.scrollStopDebounce
    )
    this.onResize = debounce(
      this.updateThresholds.bind(this),
      AnnouncementBarConfig.thresholds.resizeDebounce
    )
  }

  connectedCallback() {
    super.connectedCallback()
    this.connectScrollHide()
  }

  disconnectedCallback() {
    this.disconnectScrollHide()
    super.disconnectedCallback()
  }

  get rootElement() {
    const element = this.closest(AnnouncementBarConfig.selectors.root)
    return element instanceof HTMLElement ? element : null
  }

  get canScrollHide() {
    return (
      this.rootElement?.getAttribute(
        AnnouncementBarConfig.attributes.scrollHide
      ) === 'true'
    )
  }

  get scrollTop() {
    return Math.max(0, root.scrollY || 0)
  }

  connectScrollHide() {
    if (!this.canScrollHide) {
      this.clearScrollAttributes()
      return
    }

    this.updateThresholds()
    this._unsubscribeScroll = onThemeScroll(this.onScrollEvent)
    root.addEventListener('resize', this.onResize, { passive: true })
    this.updateScrollAttributes(this.scrollTop)
  }

  disconnectScrollHide() {
    this._unsubscribeScroll?.()
    this._unsubscribeScroll = null
    root.removeEventListener('resize', this.onResize)
  }

  updateThresholds() {
    const vh = root.innerHeight || 0
    this._cachedStickyThreshold = Math.round(
      vh * AnnouncementBarConfig.thresholds.stickyThresholdRatio
    )
    this._cachedHideThreshold = Math.round(
      vh * AnnouncementBarConfig.thresholds.hideThresholdRatio
    )
  }

  onScrollEvent(event) {
    this.scrollDirection = event.detail.direction
    this.updateScrollAttributes(event.detail.scrollY)
    this.onScrollStop()
  }

  updateScrollAttributes(currentScrollTop) {
    const pastSticky = currentScrollTop > this._cachedStickyThreshold
    const isDown = this.scrollDirection === AnnouncementBarConfig.direction.down
    const shouldHide =
      pastSticky && currentScrollTop > this._cachedHideThreshold && isDown
    const scrollState = `${pastSticky}:${shouldHide}:${isDown}`

    if (scrollState === this._lastScrollState) return

    this._lastScrollState = scrollState
    this.applyScrollAttributes(isDown, pastSticky, shouldHide)
  }

  applyScrollAttributes(isDown, pastSticky, shouldHide) {
    const rootElement = this.rootElement
    if (!rootElement) return

    rootElement.setAttribute(
      AnnouncementBarConfig.attributes.scrollDirection,
      isDown
        ? AnnouncementBarConfig.direction.down
        : AnnouncementBarConfig.direction.up
    )
    rootElement.toggleAttribute(
      AnnouncementBarConfig.attributes.scrolled,
      pastSticky
    )
    rootElement.toggleAttribute(
      AnnouncementBarConfig.attributes.hidden,
      shouldHide
    )
  }

  resetScrollAttributes() {
    const rootElement = this.rootElement
    if (!rootElement) return

    rootElement.removeAttribute(AnnouncementBarConfig.attributes.scrolled)
    rootElement.removeAttribute(AnnouncementBarConfig.attributes.hidden)
    rootElement.setAttribute(
      AnnouncementBarConfig.attributes.scrollDirection,
      AnnouncementBarConfig.direction.up
    )
  }

  clearScrollAttributes() {
    const rootElement = this.rootElement
    if (!rootElement) return

    rootElement.removeAttribute(AnnouncementBarConfig.attributes.scrolled)
    rootElement.removeAttribute(AnnouncementBarConfig.attributes.hidden)
    rootElement.removeAttribute(
      AnnouncementBarConfig.attributes.scrollDirection
    )
    this._lastScrollState = ''
  }

  onScrollStopHandler() {
    root.requestAnimationFrame(() => {
      if (this.scrollTop > this._cachedStickyThreshold) return
      if (
        !this.rootElement?.hasAttribute(
          AnnouncementBarConfig.attributes.scrolled
        )
      ) {
        return
      }

      this.resetScrollAttributes()
      this._lastScrollState = ''
    })
  }
}

if (!customElements.get('ui-announcement-bar')) {
  customElements.define('ui-announcement-bar', AnnouncementBar)
}
